import { getPricingPageProductRoute } from 'pricing-pages-lib/utils/urls';
import { PurchaseMotionCTA } from 'purchase-motions-components/components/PurchaseMotionCTA/PurchaseMotionCTA';
import Raven from 'raven-js';
import { useCallback, useMemo } from 'react';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { useEnrollPortalMutation } from '../../../__generated__/chirp/com/hubspot/fintech/discounts/rpc/PromotionsRpc.hooks';
import { GET_PRODUCT_BADGES_QUERY_FIELD_NAME as InAppBadgesQueryFieldName } from '../../../__generated__/chirp/com/hubspot/monetization/rpc/BadgeInAppService.dfc.new';
import { GET_PRODUCT_BADGES_QUERY_FIELD_NAME as PublicBadgesQueryFieldName } from '../../../__generated__/chirp/com/hubspot/monetization/rpc/BadgePublicService.dfc.new';
import { dataFetchingClient } from '../../utils/dataFetchingClient';
import { PROMOTION_MAPPING, TRIAL_PROMO_LENGTH, logHC0136Exposure, useHC0136ExperimentEffect } from '../../utils/experiments/hc0136';
import { Metrics } from '../../utils/metrics';
import { trackButtonClick, trackBuyButtonClick, trackTalkToSalesClick } from '../../v2/utils/tracking/trackingEventUtils';
import { useHighestProductLevelFromInventory } from '../hooks/inventory/useHighestProductLevel';
import { useProductInventory } from '../hooks/inventory/useProductInventory';
import { useDisplayedCurrencyCode } from '../hooks/pricing/useCurrencyDetails';
import { useProductDetails } from '../hooks/product/useProductDetails';
import { usePurchaseMotionCTAText } from '../hooks/purchasing/usePurchaseMotionCTAText';
import { usePurchaseRequestForPurchaseMotion } from '../hooks/purchasing/usePurchaseRequest';
import { useStarterPlatformUnbundling } from '../hooks/purchasing/useStarterPlatformUnbundling';
import { useTrackingFlow } from '../hooks/purchasing/useTrackingFlow';
import { useIsInAppAuth } from '../hooks/user/useUserInfo';
import { getTrackingSourceFromTrackingData } from '../utils/trackingData';
import { isValidApiName } from '../utils/typeguards';
import { jsx as _jsx } from "react/jsx-runtime";
export const PurchaseMotionCTAWrapper = ({
  productApiName,
  purchaseMotion,
  trackingData,
  location,
  displayIndex,
  purchaseRequest: purchaseRequestFromProps,
  skipInterstitial,
  children,
  disabled
}) => {
  const displayedCurrencyCode = useDisplayedCurrencyCode();
  const {
    products
  } = useProductInventory();
  const {
    highestProductLevel
  } = useHighestProductLevelFromInventory();
  const {
    productDetails
  } = useProductDetails({
    productApiName
  });
  const isAppAuth = useIsInAppAuth();
  const buttonText = usePurchaseMotionCTAText({
    purchaseMotion,
    purchaseMotionLocation: location,
    children
  });
  const ownedProducts = (products === null || products === void 0 ? void 0 : products.map(product => product.productApiName)) || [];
  const trackingSource = getTrackingSourceFromTrackingData(trackingData) || 'pricing-pages';
  const trackingFlow = useTrackingFlow({
    selectedProductLevel: productDetails === null || productDetails === void 0 ? void 0 : productDetails.productLevel,
    highestOwnedProductLevel: highestProductLevel !== null && highestProductLevel !== void 0 ? highestProductLevel : undefined
  });

  // HC0136 trial promotion experiment
  const {
    isHC0136Variation,
    isEligibleForHC0136
  } = useHC0136ExperimentEffect(productApiName);
  const [enrollPortalForPromotion] = useEnrollPortalMutation({
    ignoreResults: true
  });
  const pricingPageRoute = getPricingPageProductRoute(isAppAuth);
  const onTrialStartSuccess = useCallback(() => {
    if (isEligibleForHC0136) {
      logHC0136Exposure();
      if (isHC0136Variation) {
        const promotionId = PROMOTION_MAPPING[productApiName];
        const expiresAt = Date.now() + TRIAL_PROMO_LENGTH;
        trackButtonClick({
          action: 'click button',
          value: 'trial promotion added'
        });
        void enrollPortalForPromotion({
          variables: {
            expiresAt,
            promotionId
          }
        });
      }
    }
    void dataFetchingClient.refetchQueries({
      include: [isAppAuth ? InAppBadgesQueryFieldName : PublicBadgesQueryFieldName]
    });
  }, [productApiName, enrollPortalForPromotion, isEligibleForHC0136, isHC0136Variation, isAppAuth]);
  const purchaseRequest = usePurchaseRequestForPurchaseMotion({
    productApiName,
    purchaseMotion,
    skipInterstitial,
    // if the purchase request is already provided, we don't need to do any work to get it
    skip: !!purchaseRequestFromProps
  });
  const purchaseRequestToUse = purchaseRequestFromProps !== null && purchaseRequestFromProps !== void 0 ? purchaseRequestFromProps : purchaseRequest;
  const trackingProductNames = useMemo(() => {
    var _purchaseRequestToUse, _purchaseRequestToUse2, _purchaseRequestToUse3, _purchaseRequestToUse4;
    return [...((_purchaseRequestToUse = purchaseRequestToUse === null || purchaseRequestToUse === void 0 || (_purchaseRequestToUse2 = purchaseRequestToUse.products) === null || _purchaseRequestToUse2 === void 0 ? void 0 : _purchaseRequestToUse2.map(product => product.productApiName)) !== null && _purchaseRequestToUse !== void 0 ? _purchaseRequestToUse : []), ...((_purchaseRequestToUse3 = purchaseRequestToUse === null || purchaseRequestToUse === void 0 || (_purchaseRequestToUse4 = purchaseRequestToUse.quantityPacks) === null || _purchaseRequestToUse4 === void 0 ? void 0 : _purchaseRequestToUse4.map(quantityPack => `${readUnhandledEnumCase(quantityPack.quantityName)}_${quantityPack.parentProductApiName}`)) !== null && _purchaseRequestToUse3 !== void 0 ? _purchaseRequestToUse3 : [])];
  }, [purchaseRequestToUse]);
  const handleClick = motion => {
    switch (motion) {
      case 'BUY_TOUCHLESS':
      case 'BUY_ADDITIONAL_SEATS':
      case 'BUY_ADDITIONAL_CONTACTS':
        if (motion === 'BUY_TOUCHLESS') {
          Metrics.counter('buy-now-clicked', {
            source: trackingData.uniqueId || 'PurchaseMotionContainer',
            version: 'v2'
          }).increment();
        }

        // Purchase motion location filtering - don't track on interstitial page
        if (location === 'PRICING_PAGE_INTERSTITIAL') {
          return;
        }
        trackBuyButtonClick({
          activeCurrencyCode: displayedCurrencyCode,
          flow: trackingFlow,
          ownedProducts,
          productNames: trackingProductNames,
          purchaseMotion: motion,
          screen: trackingData.screen,
          trackingSource
        });
        break;
      case 'TALK_TO_SALES':
      case 'TALK_TO_SALES_FALLBACK':
        trackTalkToSalesClick({
          activeCurrencyCode: displayedCurrencyCode,
          flow: trackingFlow,
          ownedProducts,
          productNames: trackingProductNames,
          purchaseMotion: motion,
          screen: trackingData.screen,
          trackingSource
        });
        break;
      default:
    }
  };
  const {
    eligibleForUnbundling
  } = useStarterPlatformUnbundling(purchaseRequestToUse, purchaseMotion);
  if (!isValidApiName(productApiName)) {
    Raven.captureException(new Error(`Invalid product API name (${productApiName}) in PurchaseMotionCTAWrapper`));
    return null;
  }
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(PurchaseMotionCTA, {
      apiName: productApiName,
      "data-selenium-id": `${trackingSource}-${productApiName}-${String(purchaseMotion)}-button`,
      location: location,
      trackingData: trackingData,
      purchaseMotion: purchaseMotion,
      uiButtonProps: Object.assign({
        block: true,
        use: displayIndex === 0 ? 'primary' : 'secondary'
      }, undefined !== disabled && {
        disabled
      }),
      onClick: handleClick,
      ctaProps: {
        purchaseRequest: purchaseRequestToUse,
        skipInterstitial: skipInterstitial || eligibleForUnbundling,
        shouldDisplayAlertOnFailure: true,
        interstitialQueryParams: {
          pricingPageProductRoute: pricingPageRoute || ''
        },
        onTrialStartSuccess
      },
      children: buttonText
    })
  });
};